import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw, ChevronDown } from 'lucide-react';
import { LuTowerControl } from 'react-icons/lu';
import {
  WiDaySunny,
  WiCloudy,
  WiRain,
  WiSnow,
  WiThunderstorm,
  WiFog,
} from 'react-icons/wi';
import axios from 'axios';
import { supabase } from '../../lib/supabase';

// Hooks
// (Removed unused: useCurrentTripView, useAutoRefresh - using manual implementation)

// Components
import Sidebar from '../../components/ui/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorDisplay from '../../components/ErrorDisplay';
import WeatherCard from '../../components/pilot/WeatherCard';
import WeatherModal from '../../components/pilot/WeatherModal';
import TripHeader from './components/TripHeader';
import FAR117Bar from './components/FAR117Bar';
import FAR117DetailModal from './components/FAR117DetailModal';
import FlightProgressTimeline from './components/FlightProgressTimeline';
import FlightDetailsModal from './components/FlightDetailsModal';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ============================================================================
// 🔌 API ENDPOINTS USED BY THIS PAGE:
// ============================================================================
// 1. GET /api/v1/rotations/current?employee_id={id} - Auto-detect active rotation
// 2. GET /api/v1/rotations/{rotation_number}/compiled - Compiled rotation data
// 3. GET /api/v1/pay/calculate/rotation/{rotation_number}?employee_id={id} - Pay data
// 4. GET /api/v1/compliance/rotation/{rotation_number}?employee_id={id} - FAR 117 compliance
//
// TODO: Future endpoints needed:
// - GET /api/v1/weather/{icao} - Real-time METAR/TAF weather data
// - GET /api/v1/atc-delays/{airport} - FAA ground delay program data
// - GET /api/v1/fatigue/calculate - NASA fatigue risk model (future feature)
// ============================================================================

const USE_TEST_DATA = false; // ✅ USING LIVE DATA FROM API

// 🧪 TEST FIXTURE - For offline development (NOT USED when USE_TEST_DATA = false)
const TEST_ROTATION_FIXTURE = {
  id: 99999,
  rotation_number: "9999",
  employee_id: "TEST_USER",
  compiled_json: {
    meta: {
      rotation_number: "9999",
      base: "NYC",
      employee_id: "TEST_USER",
      employee_name: "TEST PILOT",
      position: "CA",
      equipment: "73N",
      rotation_start_date: "2026-01-29",
      rotation_end_date: "2026-01-30",
      bid_period: "Jan 2026",
      bid_period_normalized: "2026-01",
    },
    duty_periods: [
      {
        duty_sequence: 1,
        report: {
          utc: "2026-01-29T16:00:00Z",
          local_wall: "2026-01-29T11:00:00",
          zulu: "2026-01-29T16:00:00Z"
        },
        release: {
          utc: "2026-01-30T00:30:00Z",
          local_wall: "2026-01-29T19:30:00",
          zulu: "2026-01-30T00:30:00Z"
        },
        legs: [
          {
            leg_sequence: 1,
            flight_number: "DL0425",
            departure: "JFK",
            arrival: "ATL",
            sched_out: {
              utc: "2026-01-29T17:00:00Z",
              local_wall: "2026-01-29T12:00:00"
            },
            actual_out: {
              utc: "2026-01-29T17:45:00Z", // 45 min delay
              local_wall: "2026-01-29T12:45:00"
            },
            sched_in: {
              utc: "2026-01-29T19:15:00Z",
              local_wall: "2026-01-29T14:15:00"
            },
            actual_in: {
              utc: "2026-01-29T20:05:00Z", // 50 min delay
              local_wall: "2026-01-29T15:05:00"
            },
            sched_block: { minutes: 135 },
            aircraft_type: "73N",
            aircraft_registration: "N712TW"
          },
          {
            leg_sequence: 2,
            flight_number: "DL1245",
            departure: "ATL",
            arrival: "LAX",
            sched_out: {
              utc: "2026-01-29T22:00:00Z",
              local_wall: "2026-01-29T17:00:00"
            },
            actual_out: {
              utc: "2026-01-29T22:00:00Z", // On time
              local_wall: "2026-01-29T17:00:00"
            },
            sched_in: {
              utc: "2026-01-30T00:30:00Z",
              local_wall: "2026-01-29T18:30:00"
            },
            actual_in: null, // Future flight
            sched_block: { minutes: 310 },
            aircraft_type: "73N",
            aircraft_registration: "N712TW"
          }
        ]
      }
    ],
    total_sched_block_minutes: 445,
    total_actual_block_minutes: 450,
    total_duty_minutes: 510,
    far117_compliant: true,
    far117_violations: []
  }
};
// ============================================================================

/**
 * Transform API data structure to frontend expectations
 * 
 * Backend returns: sched_out.local_wall, sched_in.local_wall (ISO timestamps)
 * Frontend expects: scheduled_departure.local, scheduled_arrival.local (HH:MM strings)
 */
const transformApiData = (apiData) => {
  if (!apiData?.compiled_json?.duty_periods) return apiData;

  const extractTime = (timeObj) => {
    if (!timeObj?.local_wall) return null;
    // Extract HH:MM from ISO timestamp (e.g., "2026-01-26T19:59:00" → "19:59")
    const timePart = timeObj.local_wall.split('T')[1];
    return timePart ? timePart.substring(0, 5) : null;
  };

  const transformedData = JSON.parse(JSON.stringify(apiData)); // Deep clone

  transformedData.compiled_json.duty_periods.forEach(dp => {
    // Transform duty period report/release times
    if (dp.report) {
      dp.report = {
        local: extractTime(dp.report),
        zulu: extractTime({ local_wall: dp.report.utc }),
        base: extractTime(dp.report) // Use local as base for now
      };
    }
    if (dp.release) {
      dp.release = {
        local: extractTime(dp.release),
        zulu: extractTime({ local_wall: dp.release.utc }),
        base: extractTime(dp.release) // Use local as base for now
      };
    }

    if (dp.legs) {
      dp.legs.forEach(leg => {
        // Transform SCHEDULED departure times
        if (leg.sched_out) {
          leg.scheduled_departure = {
            local: extractTime(leg.sched_out),
            zulu: extractTime({ local_wall: leg.sched_out.utc }),
            base: extractTime(leg.sched_out) // Use local as base for now
          };
        }
        
        // Transform ACTUAL departure times (Out = pushback, Off = wheels off)
        if (leg.actual_out) {
          leg.actual_departure = {
            local: extractTime(leg.actual_out),
            zulu: extractTime({ local_wall: leg.actual_out.utc }),
            base: extractTime(leg.actual_out)
          };
        } else if (leg.actual_off) {
          // Fallback to wheels-off if gate-out not available
          leg.actual_departure = {
            local: extractTime(leg.actual_off),
            zulu: extractTime({ local_wall: leg.actual_off.utc }),
            base: extractTime(leg.actual_off)
          };
        }
        
        // Transform SCHEDULED arrival times
        if (leg.sched_in) {
          leg.scheduled_arrival = {
            local: extractTime(leg.sched_in),
            zulu: extractTime({ local_wall: leg.sched_in.utc }),
            base: extractTime(leg.sched_in) // Use local as base for now
          };
        }
        
        // Transform ACTUAL arrival times (On = wheels on, In = gate arrival)
        if (leg.actual_in) {
          leg.actual_arrival = {
            local: extractTime(leg.actual_in),
            zulu: extractTime({ local_wall: leg.actual_in.utc }),
            base: extractTime(leg.actual_in)
          };
        } else if (leg.actual_on) {
          // Fallback to wheels-on if gate-in not available
          leg.actual_arrival = {
            local: extractTime(leg.actual_on),
            zulu: extractTime({ local_wall: leg.actual_on.utc }),
            base: extractTime(leg.actual_on)
          };
        }
      });
    }
  });

  return transformedData;
};

/**
 * Current-Trip Mobile Widget View
 * 
 * Mobile-first, widget-style landing page for pilots
 * Inspired by: Flighty app + Android widgets
 * Goal: Instant, glanceable flight information
 * 
 * Key Features:
 * - Auto-detect current rotation (today's date)
 * - Mobile-optimized cards (not desktop timeline)
 * - Real-time updates every 30s (active flights only)
 * - Auto-scroll to current flight
 * - Pull-to-refresh gesture
 * 
 * Pattern: Copied from rotation-breakdown/ (GOLDEN RULE 0 compliant!)
 */

const CurrentTrip = () => {
  
  // ✅ GOLDEN RULE: Use employee_id with dev fallback (copied from rotation-breakdown)
  const [employeeId, setEmployeeId] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  
  // Auto-detect rotation state
  const [rotationNumber, setRotationNumber] = useState(null);
  const [detectingRotation, setDetectingRotation] = useState(true);
  
  // Data states (parallel loading like rotation-breakdown)
  const [compiledData, setCompiledData] = useState(null);
  const [payData, setPayData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Current flight detection
  const [currentFlightIndex, setCurrentFlightIndex] = useState(0);

  // Weather modal state
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [weatherSlideFrom, setWeatherSlideFrom] = useState('left');
  
  // ✅ Step 1: Load user settings (with dev fallback)
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.warn('[DEV MODE] No authenticated user, using fallback employee_id');
          setEmployeeId('141986'); // Dev mode fallback - GOLDEN RULE
          return;
        }

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('employee_id, employee_name')
          .eq('id', user.id)
          .single();

        if (profile?.employee_id) {
          setEmployeeId(profile.employee_id);
          setUserSettings(profile);
        } else {
          setEmployeeId('141986'); // Fallback if no profile
        }
      } catch (err) {
        console.error('[CurrentTrip] Error loading user settings:', err);
        setEmployeeId('141986'); // Fallback on error
      }
    };

    loadUserSettings();
  }, []);

  // ✅ Step 2: AUTO-DETECT current rotation (NEW!)
  useEffect(() => {
    // 🧪 SKIP API DETECTION WHEN USING TEST DATA
    if (USE_TEST_DATA) {
      setRotationNumber('9999'); // Set test rotation number
      return;
    }
    
    if (!employeeId) return;

    const detectCurrentRotation = async () => {
      setDetectingRotation(true);
      setError(null);

      try {
        // Call new endpoint to auto-detect rotation active today
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/rotations/current`,
          { params: { employee_id: employeeId } }
        );

        const data = response.data;
        console.log('[CurrentTrip] Detected rotation:', data.rotation_number);
        setRotationNumber(data.rotation_number); // ✅ GOLDEN RULE 0: Use rotation_number!
      } catch (err) {
        console.error('[CurrentTrip] Error detecting rotation:', err);
        
        if (err.response?.status === 404) {
          setError({
            title: 'No Active Rotation',
            message: 'You are not on a trip today. Enjoy your day off! 🌴',
            type: 'info'
          });
        } else {
          setError({
            title: 'Detection Failed',
            message: err.message || 'Could not detect current rotation',
            type: 'error'
          });
        }
      } finally {
        setDetectingRotation(false);
      }
    };

    detectCurrentRotation();
  }, [employeeId]);

  // ✅ Step 3: PARALLEL LOADING (copied from rotation-breakdown pattern)
  useEffect(() => {
    if (!rotationNumber && !USE_TEST_DATA) return;
    if (USE_TEST_DATA) {
      // 🧪 USE TEST DATA INSTEAD OF API
      console.log(`[CurrentTrip] 🧪 USING TEST DATA (rotation 9999)`);
      console.log(`[CurrentTrip] 🔍 BEFORE: loading=${loading}, detectingRotation=${detectingRotation}`);
      
      setCompiledData(TEST_ROTATION_FIXTURE);
      setPayData({
        total_credit_hours: 7.42,
        total_pay_hours: 7.42,
        credit_breakdown: { base: 7.42 }
      });
      setComplianceData({
        is_compliant: true,
        violations: []
      });
      detectCurrentFlight(TEST_ROTATION_FIXTURE);
      
      // 🚨 CRITICAL: Clear BOTH loading states
      setLoading(false);
      setDetectingRotation(false);
      
      console.log(`[CurrentTrip] ✅ AFTER: setLoading(false), setDetectingRotation(false) called`);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // ✅ GOLDEN RULE 0: Use rotation_number (NOT database ID!)
        console.log(`[CurrentTrip] Loading data for rotation ${rotationNumber}...`);

        // Parallel API calls (3x faster than sequential!)
        const [compiledRes, payRes, complianceRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/v1/rotations/${rotationNumber}/compiled`),
          axios.get(`${API_BASE_URL}/api/v1/pay/calculate/rotation/${rotationNumber}`, {
            params: { employee_id: employeeId }
          }),
          axios.get(`${API_BASE_URL}/api/v1/compliance/rotation/${rotationNumber}`, {
            params: { employee_id: employeeId }
          })
        ]);

        // ✅ TRANSFORM API DATA: Map backend structure → frontend expectations
        const transformedData = transformApiData(compiledRes.data);
        
        setCompiledData(transformedData);
        setPayData(payRes.data);
        setComplianceData(complianceRes.data);

        // Auto-detect current flight based on time
        detectCurrentFlight(transformedData);

        console.log('[CurrentTrip] ✅ All data loaded successfully');
      } catch (err) {
        console.error('[CurrentTrip] Error loading data:', err);
        setError({
          title: 'Load Failed',
          message: err.message || 'Failed to load rotation data',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [rotationNumber, employeeId]);

  // ✅ Step 4: AUTO-REFRESH for active flights (30s interval)
  useEffect(() => {
    if (!rotationNumber || !employeeId) return;

    // Only refresh if we have an active flight within 6 hours
    const shouldAutoRefresh = () => {
      if (!compiledData?.compiled_json?.duty_periods) return false;
      
      const now = new Date();
      const sixHoursFromNow = new Date(now.getTime() + 6 * 60 * 60 * 1000);
      
      // Check if any flight is within 6 hours
      for (const dp of compiledData.compiled_json.duty_periods) {
        for (const leg of dp.legs || []) {
          // Use original sched_out for date comparison
          const depTime = leg.sched_out?.utc ? new Date(leg.sched_out.utc) : null;
          if (depTime && depTime <= sixHoursFromNow && depTime >= now) {
            return true;
          }
        }
      }
      return false;
    };

    if (!shouldAutoRefresh()) {
      console.log('[CurrentTrip] No active flights - skipping auto-refresh');
      return;
    }

    console.log('[CurrentTrip] 🔄 Starting auto-refresh (30s interval)');
    const interval = setInterval(async () => {
      try {
        const [compiled, pay, compliance] = await Promise.all([
          getCompiledRotation(rotationNumber),
          getPayData(rotationNumber, employeeId),
          getComplianceData(rotationNumber, employeeId)
        ]);
        setCompiledData(compiled);
        setPayData(pay);
        setComplianceData(compliance);
        console.log('[CurrentTrip] ✅ Auto-refresh complete');
      } catch (err) {
        console.error('[CurrentTrip] Auto-refresh failed:', err);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [rotationNumber, employeeId, compiledData]);

  // Detect current flight based on time
  const detectCurrentFlight = (compiled) => {
    if (!compiled?.compiled_json?.duty_periods) return;
    
    const now = new Date();
    let flightIndex = 0;
    let found = false;

    for (const dp of compiled.compiled_json.duty_periods) {
      for (const leg of dp.legs || []) {
        // Use original sched_out/sched_in for date comparison (has full timestamps)
        const depTime = leg.sched_out?.utc ? new Date(leg.sched_out.utc) : null;
        const arrTime = leg.sched_in?.utc ? new Date(leg.sched_in.utc) : null;
        
        if (!depTime || !arrTime) {
          flightIndex++;
          continue;
        }
        
        // If we're between departure and arrival, this is current flight
        if (now >= depTime && now <= arrTime) {
          setCurrentFlightIndex(flightIndex);
          found = true;
          console.log(`[CurrentTrip] 📍 Current flight detected: Index ${flightIndex}`);
          break;
        }
        
        // If flight is upcoming (within 2 hours), select it
        const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
        if (depTime <= twoHoursFromNow && depTime >= now) {
          setCurrentFlightIndex(flightIndex);
          found = true;
          console.log(`[CurrentTrip] 📍 Upcoming flight detected: Index ${flightIndex}`);
          break;
        }
        
        flightIndex++;
      }
      if (found) break;
    }

    if (!found) {
      console.log('[CurrentTrip] No current/upcoming flight - showing first flight');
    }
  };

  // Force refresh (pull-to-refresh trigger)
  const handleRefresh = async () => {
    if (!rotationNumber || !employeeId) return;
    
    setLoading(true);
    try {
      const [compiledRes, payRes, complianceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/v1/rotations/${rotationNumber}/compiled`),
        axios.get(`${API_BASE_URL}/api/v1/pay/calculate/rotation/${rotationNumber}`, {
          params: { employee_id: employeeId }
        }),
        axios.get(`${API_BASE_URL}/api/v1/compliance/rotation/${rotationNumber}`, {
          params: { employee_id: employeeId }
        })
      ]);

      const compiled = transformApiData(compiledRes.data);
      setCompiledData(compiled);
      setPayData(payRes.data);
      setComplianceData(complianceRes.data);
      detectCurrentFlight(compiled);
    } catch (err) {
      setError({
        title: 'Refresh Failed',
        message: err.message || 'Failed to refresh data',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get compiled rotation (used in auto-refresh)
  const getCompiledRotation = async (rotation) => {
    // 🧪 SKIP API CALL WHEN USING TEST DATA
    if (USE_TEST_DATA) {
      return TEST_ROTATION_FIXTURE;
    }
    const res = await axios.get(`${API_BASE_URL}/api/v1/rotations/${rotation}/compiled`);
    return transformApiData(res.data);
  };

  // Helper function to get pay data (used in auto-refresh)
  const getPayData = async (rotation, employee) => {
    // 🧪 SKIP API CALL WHEN USING TEST DATA
    if (USE_TEST_DATA) {
      return {
        total_credit_hours: 7.42,
        total_pay_hours: 7.42,
        credit_breakdown: { base: 7.42 }
      };
    }
    const res = await axios.get(`${API_BASE_URL}/api/v1/pay/calculate/rotation/${rotation}`, {
      params: { employee_id: employee }
    });
    return res.data;
  };

  // Helper function to get compliance data (used in auto-refresh)
  const getComplianceData = async (rotation, employee) => {
    // 🧪 SKIP API CALL WHEN USING TEST DATA
    if (USE_TEST_DATA) {
      return {
        is_compliant: true,
        violations: []
      };
    }
    const res = await axios.get(`${API_BASE_URL}/api/v1/compliance/rotation/${rotation}`, {
      params: { employee_id: employee }
    });
    return res.data;
  };

  // ✅ PROTOTYPE FEATURE: Timezone toggle (L/Z/B)
  const [timeZoneIndex, setTimeZoneIndex] = useState(0); // 0=Local, 1=Zulu, 2=Base
  const timeZones = ['L', 'Z', 'B'];

  // ✅ DOUBLE-EXPAND: 3-state accordion for Rest of Rotation sections (0=collapsed, 1=basic, 2=granular)
  const [expandedFlight, setExpandedFlight] = useState(0);
  const [expandedHotel, setExpandedHotel] = useState(0);
  const [expandedDutyDay, setExpandedDutyDay] = useState(0);

  // Helper to cycle through 3 expansion levels
  const toggleExpandLevel = (current) => (current + 1) % 3;

  const toggleTimeZone = () => {
    setTimeZoneIndex((prev) => (prev + 1) % 3);
  };
  
  const getCurrentTime = () => {
    const now = new Date();
    if (timeZoneIndex === 0) {
      // Local time
      return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } else if (timeZoneIndex === 1) {
      // Zulu (UTC)
      return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    } else {
      // Base time (using crew base - for now, same as local)
      return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
  };
  
  const getTimeWithZone = (time) => {
    return time ? `${time}${timeZones[timeZoneIndex]}` : '--:--';
  };

  const getTimeFromZone = (timeObj) => {
    if (!timeObj) return '--:--';
    if (timeZoneIndex === 0) return getTimeWithZone(timeObj.local);
    if (timeZoneIndex === 1) return getTimeWithZone(timeObj.zulu);
    return getTimeWithZone(timeObj.base);
  };

  const getRawTimeFromZone = (timeObj) => {
    if (!timeObj) return null;
    if (timeZoneIndex === 0) return timeObj.local;
    if (timeZoneIndex === 1) return timeObj.zulu;
    return timeObj.base;
  };

  // Weather modal handlers
  // 🚧 TODO: Replace with real weather API
  // Future endpoint: GET /api/v1/weather/{icao}
  // Returns: { category: 'VFR'|'MVFR'|'IFR'|'LIFR', conditions: {...}, metar: '...' }
  // Currently using static mapping for UI development
  const getWeatherIcon = (airportCode) => {
    // Static fallback - will be replaced when weather API is implemented
    const weatherMap = {
      'JFK': { icon: WiDaySunny, color: 'text-green-400', category: 'VFR' },
      'KJFK': { icon: WiDaySunny, color: 'text-green-400', category: 'VFR' },
      'ATL': { icon: WiCloudy, color: 'text-blue-400', category: 'MVFR' },
      'KATL': { icon: WiCloudy, color: 'text-blue-400', category: 'MVFR' },
      'LAX': { icon: WiDaySunny, color: 'text-green-400', category: 'VFR' },
      'KLAX': { icon: WiDaySunny, color: 'text-green-400', category: 'VFR' },
    };
    return weatherMap[airportCode] || { icon: WiCloudy, color: 'text-gray-400', category: 'UNK' };
  };

  const handleWeatherClick = (airportCode, slideFrom) => {
    console.log(`[CurrentTrip] Opening weather modal for ${airportCode}`);
    setSelectedAirport(airportCode);
    setWeatherSlideFrom(slideFrom);
    setWeatherModalOpen(true);
  };

  const handleWeatherClose = () => {
    setWeatherModalOpen(false);
  };

  const calculateDelayMinutes = (scheduledUtc, actualUtc) => {
    if (!scheduledUtc || !actualUtc) return 0;
    const scheduled = new Date(scheduledUtc);
    const actual = new Date(actualUtc);
    const delta = Math.floor((actual - scheduled) / 60000);
    return Math.max(0, delta);
  };

  // 🚧 TODO: ATC Ground Delay Program data
  // Future: Fetch from FAA ATCSCC or FlightAware API
  // Expected in compiledData.atc_delays[airport] = { minutes: number, reason: string }
  // Currently returns 0 if no data present
  const getAtcDelayMinutes = (airportCode) => {
    if (!airportCode) return 0;
    const atcDelays = compiledData?.atc_delays || {};
    const delayEntry = atcDelays[airportCode];
    if (typeof delayEntry === 'number') return delayEntry;
    if (typeof delayEntry?.minutes === 'number') return delayEntry.minutes;
    if (typeof delayEntry?.delay_minutes === 'number') return delayEntry.delay_minutes;
    return 0;
  };

  const getTowerColorClass = (delayMinutes) => {
    if (delayMinutes >= 90) return 'text-red-500';
    if (delayMinutes >= 60) return 'text-orange-500';
    if (delayMinutes >= 30) return 'text-yellow-500';
    return null;
  };

  const getAirportGradientColor = (delayMinutes, flightStatus, arrivalUtc) => {
    if (flightStatus === 'COMPLETED' && arrivalUtc) {
      const now = new Date();
      const arrivalTime = new Date(arrivalUtc);
      if (now - arrivalTime > 60 * 60 * 1000) {
        return 'text-gray-400';
      }
    }

    if (delayMinutes >= 90) return 'text-red-500';
    if (delayMinutes >= 60) return 'text-orange-500';
    if (delayMinutes >= 30) return 'text-yellow-500';
    if (delayMinutes > 0) return 'text-green-500';
    return 'text-white';
  };

  const getBorderColorFromDelay = (delayMinutes) => {
    if (delayMinutes >= 90) return 'red';
    if (delayMinutes >= 60) return 'orange';
    if (delayMinutes >= 30) return 'yellow';
    return 'green';
  };

  // Modal state
  const [showFAR117Modal, setShowFAR117Modal] = useState(false);
  const [showFlightModal, setShowFlightModal] = useState(false);

  // Loading state
  if (detectingRotation || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <LoadingSpinner message={detectingRotation ? "Detecting current rotation..." : "Loading flight data..."} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <div className="main-content p-4 md:p-6">
          <ErrorDisplay 
            title={error.title}
            message={error.message}
            type={error.type}
            onRetry={error.type === 'error' ? () => window.location.reload() : null}
          />
        </div>
      </div>
    );
  }

  // No data state
  if (!compiledData) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <div className="main-content p-4 md:p-6">
          <div className="text-center text-muted-foreground">
            <p>No rotation data available</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Extract rotation meta data
  const getMeta = () => compiledData?.compiled_json?.meta || {};
  const getFirstFlight = () => compiledData?.compiled_json?.duty_periods?.[0]?.legs?.[0] || {};
  const getComplianceSummary = () => complianceData?.analysis || {};
  const getRotationPreview = () => {
    const dutyPeriods = compiledData?.compiled_json?.duty_periods || [];
    const allLegs = [];
    let flightIdx = 0;
    let currentDutyIndex = 0;
    let currentLegIndex = 0;

    dutyPeriods.forEach((dp, dpIdx) => {
      (dp.legs || []).forEach((leg, legIdx) => {
        if (flightIdx === currentFlightIndex) {
          currentDutyIndex = dpIdx;
          currentLegIndex = legIdx;
        }
        allLegs.push({
          ...leg,
          dutyPeriod: dp,
          dutyIndex: dpIdx,
          legIndex: legIdx,
          globalIndex: flightIdx
        });
        flightIdx += 1;
      });
    });

    return {
      dutyPeriods,
      allLegs,
      currentDutyIndex,
      currentLegIndex
    };
  };
  const formatInstantTime = (instant) => {
    if (!instant) return '--:--';
    const preferred = timeZoneIndex === 1
      ? (instant.utc || instant.zulu)
      : timeZoneIndex === 2
      ? (instant.base || instant.base_wall)
      : (instant.local || instant.local_wall);
    const value = preferred || instant.local || instant.local_wall || instant.utc || instant.zulu || instant.base;
    if (!value) return '--:--';

    if (typeof value === 'string' && value.includes('T')) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        const time = date.toISOString().slice(11, 16);
        const suffix = timeZoneIndex === 1 ? 'Z' : timeZoneIndex === 2 ? 'B' : 'L';
        return `${time}${suffix}`;
      }
    }

    if (typeof value === 'string') {
      const suffix = timeZoneIndex === 1 ? 'Z' : timeZoneIndex === 2 ? 'B' : 'L';
      return value.length === 5 ? `${value}${suffix}` : value;
    }

    return '--:--';
  };
  const formatInstantDate = (instant) => {
    if (!instant) return '--';
    const value = instant.local || instant.local_wall || instant.utc || instant.zulu || instant.base || instant;
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString().slice(0, 10);
      }
      return value;
    }
    return '--';
  };
  const getCurrentFlightContext = () => {
    let flightIdx = 0;
    let currentFlight = null;
    let currentDutyPeriod = null;
    let previousFlight = null;

    for (const dp of compiledData?.compiled_json?.duty_periods || []) {
      for (const leg of dp.legs || []) {
        if (flightIdx === currentFlightIndex) {
          currentFlight = leg;
          currentDutyPeriod = dp;
          break;
        }
        previousFlight = leg; // Track the flight before current
        flightIdx++;
      }
      if (currentFlight) break;
    }

    return { currentFlight, currentDutyPeriod, previousFlight };
  };

  const firstFlight = getFirstFlight();
  const { currentFlight, currentDutyPeriod, previousFlight } = getCurrentFlightContext();
  const rotationPreview = getRotationPreview();
  const nextFlight = rotationPreview.allLegs[currentFlightIndex + 1] || null;
  const nextDutyPeriod = rotationPreview.dutyPeriods[rotationPreview.currentDutyIndex + 1] || null;
  const nextHotelDutyPeriod = currentDutyPeriod?.layover_hotel_name ? currentDutyPeriod : nextDutyPeriod;
  const depActualUtc = firstFlight?.actual_out?.utc || firstFlight?.actual_off?.utc || null;
  const depSchedUtc = firstFlight?.sched_out?.utc || null;
  const arrActualUtc = firstFlight?.actual_in?.utc || firstFlight?.actual_on?.utc || null;
  const arrSchedUtc = firstFlight?.sched_in?.utc || null;

  const depDelayMinutes = calculateDelayMinutes(depSchedUtc, depActualUtc);
  const arrDelayMinutes = calculateDelayMinutes(arrSchedUtc, arrActualUtc);
  const flightDelayMinutes = Math.max(depDelayMinutes, arrDelayMinutes);

  const now = new Date();
  const schedOutTime = depSchedUtc ? new Date(depSchedUtc) : null;
  const schedInTime = arrSchedUtc ? new Date(arrSchedUtc) : null;
  const flightStatus = schedOutTime && schedInTime
    ? (now >= schedInTime ? 'COMPLETED' : now >= schedOutTime ? 'EN ROUTE' : 'SCHEDULED')
    : 'SCHEDULED';

  const inboundStatusColor = flightDelayMinutes >= 90 ? 'text-red-500'
    : flightDelayMinutes >= 60 ? 'text-orange-500'
    : flightDelayMinutes >= 30 ? 'text-yellow-500'
    : flightStatus === 'COMPLETED' ? 'text-gray-400'
    : 'text-green-500';
  const inboundStatusMessage = flightDelayMinutes > 0 ? `Inbound Delayed +${flightDelayMinutes}m` : 'Inbound On Time';

  // ATC delays from API (TODO: wire to real FAA data when available)
  const atcDelayDepMinutes = getAtcDelayMinutes(firstFlight?.departure);
  const atcDelayArrMinutes = getAtcDelayMinutes(firstFlight?.arrival);
  const showAtcDelayBanner = atcDelayArrMinutes > 0 || atcDelayDepMinutes > 0;

  const delayBorderColor = getBorderColorFromDelay(flightDelayMinutes);

  // ✅ Main widget view - PROTOTYPE DESIGN
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <Sidebar />
      
      {/* ✅ CRITICAL: main-content class for sidebar margin */}
      <div className="main-content">
        {/* Mobile-optimized container - Prototype layout */}
        <div className="max-w-2xl mx-auto p-4 space-y-3">
          
          {/* ✅ NEW: Trip Header - Time and Rotation Number */}
          <TripHeader 
            rotationNumber={rotationNumber}
            compiledData={compiledData}
            timeZoneIndex={timeZoneIndex}
            onToggleTimeZone={toggleTimeZone}
          />

          {/* ✅ NEW: Flight Progress Timeline - OOOI Phases */}
          <FlightProgressTimeline 
            currentFlight={currentFlight}
            previousFlight={previousFlight}
            onShowDetails={() => setShowFlightModal(true)}
            className="mb-4"
          />

          {/* ✅ HERO CARD - Flight time, status, route with DYNAMIC BORDER based on delay */}
          <div
            className="bg-slate-800 border-2 rounded-xl p-4 backdrop-blur shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
            style={{
              // Border color based on flight delay: green (on-time) → yellow (30m) → orange (60m) → red (90m+)
              borderColor: delayBorderColor === 'red' ? 'rgba(239, 68, 68, 0.5)' :
                          delayBorderColor === 'orange' ? 'rgba(249, 115, 22, 0.5)' :
                          delayBorderColor === 'yellow' ? 'rgba(234, 179, 8, 0.5)' :
                          delayBorderColor === 'green' ? 'rgba(34, 197, 94, 0.5)' :
                          'rgba(107, 114, 128, 0.5)' // gray default
            }}
          >
            {/* Top bar - report time (left) + refresh (right) */}
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm text-gray-300">
                <span className="uppercase tracking-wide">Report</span>
                <span className="ml-2 font-mono text-base text-gray-100">
                  {formatInstantTime(currentDutyPeriod?.report) || '--:--'}
                </span>
              </div>
              <button 
                onClick={handleRefresh}
                disabled={loading}
                className="text-cyan-400 hover:text-white disabled:opacity-50"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* ✅ PROTOTYPE FEATURE: ATC Delays Banner (conditional) */}
            {showAtcDelayBanner && (
              <div className="bg-red-500/15 border border-red-500/35 rounded-lg p-2 mb-4 text-center">
                <div className="text-red-400 text-sm font-bold">
                  {(firstFlight?.arrival || 'ARR')} ATC Delays
                </div>
              </div>
            )}

            {/* Flight number and route with gates */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {/* Departure */}
              <div className="text-center">
                {/* Weather Icon */}
                {(() => {
                  const depAirport = firstFlight?.departure || 'DEP';
                  const weatherInfo = getWeatherIcon(depAirport);
                  const WeatherIcon = weatherInfo.icon;
                  return (
                    <button
                      onClick={() => handleWeatherClick(depAirport, 'left')}
                      className={`${weatherInfo.color} hover:scale-110 transition-transform mb-1`}
                      title={`${depAirport} Weather (${weatherInfo.category})`}
                    >
                      <WeatherIcon size={28} />
                    </button>
                  );
                })()}
                <div className={`text-4xl font-bold mb-1 ${getAirportGradientColor(flightDelayMinutes, flightStatus, arrActualUtc)}`}>
                  {firstFlight?.departure || 'DEP'}
                </div>
                <div className="text-gray-400 text-xs">
                  Gate: {firstFlight?.departure_gate || '14'}
                </div>
              </div>

              {/* Flight number center */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-cyan-400 font-bold text-2xl mb-2">
                  {firstFlight?.flight_number || 'DL 12345'}
                </div>
                <div className="bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-bold px-3 py-1 rounded inline-block">
                  EN ROUTE
                </div>
              </div>

              {/* Arrival */}
              <div className="text-center">
                {/* Weather Icon */}
                {(() => {
                  const arrAirport = firstFlight?.arrival || 'ARR';
                  const weatherInfo = getWeatherIcon(arrAirport);
                  const WeatherIcon = weatherInfo.icon;
                  return (
                    <button
                      onClick={() => handleWeatherClick(arrAirport, 'right')}
                      className={`${weatherInfo.color} hover:scale-110 transition-transform mb-1`}
                      title={`${arrAirport} Weather (${weatherInfo.category})`}
                    >
                      <WeatherIcon size={28} />
                    </button>
                  );
                })()}
                <div className={`text-4xl font-bold mb-1 ${getAirportGradientColor(flightDelayMinutes, flightStatus, arrActualUtc)}`}>
                  {firstFlight?.arrival || 'ARR'}
                </div>
                <div className="text-gray-400 text-xs">
                  Gate: {firstFlight?.arrival_gate || '72'}
                </div>
              </div>
            </div>

            {/* Times row with timezone-aware display */}
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div>
                <div className="text-gray-400 text-xs mb-1 uppercase tracking-wider">DEP TIME</div>
                <div className="flex items-center justify-center gap-1">
                  {(() => {
                    const atcColor = getTowerColorClass(atcDelayDepMinutes);
                    return atcColor ? (
                      <LuTowerControl className={`w-4 h-4 ${atcColor}`} title={`ATC Delay: ${atcDelayDepMinutes} min`} />
                    ) : null;
                  })()}
                </div>
                <div className="space-y-0.5">
                  {(() => {
                    const scheduled = getRawTimeFromZone(firstFlight?.scheduled_departure);
                    const actual = getRawTimeFromZone(firstFlight?.actual_departure);
                    return scheduled && actual && scheduled !== actual ? (
                      <div className="text-red-400 text-xs font-['Courier_New',monospace] line-through">
                        {getTimeFromZone(firstFlight?.scheduled_departure)}
                      </div>
                    ) : null;
                  })()}
                  <div className="text-green-400 font-['Courier_New',monospace] font-bold text-xl cursor-pointer hover:text-cyan-400 transition-colors">
                    {firstFlight?.actual_departure ? getTimeFromZone(firstFlight?.actual_departure) : getTimeFromZone(firstFlight?.scheduled_departure)}
                  </div>
                </div>
              </div>
              <div className="bg-slate-700/50 rounded py-2">
                <div className="text-gray-400 text-xs mb-1 uppercase tracking-wider">FLIGHT TIME</div>
                <div className="text-blue-400 font-['Courier_New',monospace] font-bold text-xl">
                  {(() => {
                    const mins = compiledData?.compiled_json?.duty_periods?.[0]?.legs?.[0]?.sched_block?.minutes || 112;
                    const hrs = Math.floor(mins / 60);
                    const min = mins % 60;
                    return `${hrs}:${min.toString().padStart(2, '0')}`;
                  })()}
                </div>
              </div>
              <div className={showAtcDelayBanner ? 'bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded p-2' : ''}>
                <div className="text-gray-400 text-xs mb-1 uppercase tracking-wider">ARR TIME</div>
                <div className="flex items-center justify-center gap-1">
                  {(() => {
                    const atcColor = getTowerColorClass(atcDelayArrMinutes);
                    return atcColor ? (
                      <LuTowerControl className={`w-4 h-4 ${atcColor}`} title={`ATC Delay: ${atcDelayArrMinutes} min`} />
                    ) : null;
                  })()}
                </div>
                <div className="space-y-0.5">
                  {(() => {
                    const scheduled = getRawTimeFromZone(firstFlight?.scheduled_arrival);
                    const actual = getRawTimeFromZone(firstFlight?.actual_arrival);
                    return scheduled && actual && scheduled !== actual ? (
                      <div className="text-red-400 text-xs font-['Courier_New',monospace] line-through">
                        {getTimeFromZone(firstFlight?.scheduled_arrival)}
                      </div>
                    ) : null;
                  })()}
                  <div className={`font-['Courier_New',monospace] font-bold text-xl cursor-pointer hover:text-cyan-400 transition-colors ${showAtcDelayBanner ? 'text-orange-400' : 'text-green-400'}`}>
                    {firstFlight?.actual_arrival ? getTimeFromZone(firstFlight?.actual_arrival) : getTimeFromZone(firstFlight?.scheduled_arrival)}
                  </div>
                </div>
              </div>
            </div>

            {/* FAR 117 Compliance Bar */}
            <div className="bg-slate-700/50 rounded-lg mb-3 overflow-hidden">
              <FAR117Bar
                currentDutyPeriod={currentDutyPeriod}
                currentFlight={currentFlight}
                timeZoneIndex={timeZoneIndex}
                baseAirport={compiledData?.compiled_json?.meta?.base}
                onClickDetail={() => setShowFAR117Modal(true)}
              />
            </div>

            {/* NASA Fatigue Risk Bar */}
            {(() => {
              // Get fatigue score from compliance data or calculate from duty time
              const fatigueScore = complianceData?.fatigue_score ??
                                   compiledData?.compiled_json?.fatigue_score ?? 0;
              const maxScore = 10;
              const percentage = Math.min((fatigueScore / maxScore) * 100, 100);

              // Determine fatigue level and colors
              const getFatigueLevel = (score) => {
                if (score <= 3) return { level: 'LOW', color: 'text-green-400', barColor: 'bg-green-500', message: 'MINIMAL FATIGUE - WELL RESTED' };
                if (score <= 6) return { level: 'MODERATE', color: 'text-yellow-400', barColor: 'bg-yellow-500', message: 'MODERATE FATIGUE - TAKE PRECAUTIONS' };
                return { level: 'HIGH', color: 'text-orange-400', barColor: 'bg-orange-500', message: 'HIGH FATIGUE - EXTREME CAUTION' };
              };

              const fatigueConfig = getFatigueLevel(fatigueScore);

              return (
                <div className="bg-slate-700/50 rounded-lg p-3 cursor-pointer hover:bg-slate-700/70 transition-colors">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-gray-400 text-xs uppercase tracking-wider">NASA FATIGUE RISK</div>
                    <div className={`text-sm font-bold ${fatigueConfig.color}`}>
                      {fatigueScore}/{maxScore} - {fatigueConfig.level}
                    </div>
                  </div>

                  {/* Gradient bar with ball indicator */}
                  <div className="relative mb-2">
                    <div className="w-full h-6 bg-slate-600 rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all duration-300"
                        style={{
                          width: `${Math.max(percentage, 5)}%`,
                          background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 30%, #f59e0b 60%, #f97316 80%, #ef4444 100%)'
                        }}
                      ></div>
                      {/* Text overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white text-xs font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          {fatigueConfig.message}
                        </span>
                      </div>
                    </div>

                    {/* Ball indicator */}
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-2 ${
                        fatigueScore <= 3 ? 'border-green-400' :
                        fatigueScore <= 6 ? 'border-yellow-400' : 'border-orange-400'
                      } transition-all duration-300`}
                      style={{ left: `calc(${Math.max(percentage, 5)}% - 10px)` }}
                    ></div>
                  </div>

                  {/* Range labels */}
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-green-400">LOW</span>
                    <span className="text-yellow-400">MODERATE</span>
                    <span className="text-orange-400">HIGH</span>
                  </div>
                </div>
              );
            })()}

          </div>

          {/* ✅ ROTATION OVERVIEW - REST OF TRIP (Double-Expand Accordion) */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
            <div className="text-cyan-400 text-sm uppercase font-semibold tracking-wider text-center">
              Rest of Rotation
            </div>
            <div className="text-gray-400 text-xs text-center">
              Tap once for basics • Tap again for full details
            </div>

            {/* ✅ NEXT FLIGHT - 3-LEVEL ACCORDION */}
            <div 
              className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 cursor-pointer hover:bg-slate-900/60 transition-colors"
              onClick={() => setExpandedFlight(toggleExpandLevel(expandedFlight))}
            >
              {/* Summary (Always Visible) */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Next Flight</div>
                  <div className="text-white font-semibold">
                    {nextFlight
                      ? `${nextFlight.flight_number || 'Flight'} • ${nextFlight.departure || 'DEP'} → ${nextFlight.arrival || 'ARR'}`
                      : 'No upcoming flights'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-cyan-400 text-sm font-mono">
                    {nextFlight?.scheduled_departure ? getTimeFromZone(nextFlight.scheduled_departure) : '--:--'}
                  </div>
                  {/* Chevron Icon - Rotates Based on Expansion Level */}
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform ${
                      expandedFlight === 1 ? 'rotate-180' : expandedFlight === 2 ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Level 1: Basic Details (Route, Times, Equipment) */}
              {expandedFlight >= 1 && nextFlight && (
                <div className="mt-3 space-y-2 text-sm text-gray-300 border-t border-slate-600 pt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Route</span>
                    <span>{nextFlight.departure || 'DEP'} → {nextFlight.arrival || 'ARR'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Scheduled</span>
                    <span className="font-mono">
                      {getTimeFromZone(nextFlight.scheduled_departure)} - {getTimeFromZone(nextFlight.scheduled_arrival)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Equipment</span>
                    <span className="font-mono">
                      {nextFlight.aircraft_type || nextFlight.aircraft_model || getMeta().equipment || '—'}
                    </span>
                  </div>
                  {nextFlight.is_deadhead && (
                    <div className="text-amber-400 text-xs font-semibold">✈️ DEADHEAD</div>
                  )}
                </div>
              )}

              {/* Level 2: Granular Details (Block Time, Actual Times, Flight Info) */}
              {expandedFlight === 2 && nextFlight && (
                <div className="mt-2 space-y-2 text-xs text-gray-400 bg-slate-950/50 p-2 rounded border border-slate-600">
                  <div className="text-cyan-300 font-semibold mb-2">Extended Details</div>
                  {nextFlight.sched_block_minutes && (
                    <div className="flex justify-between">
                      <span>Block Time</span>
                      <span className="font-mono">{Math.floor(nextFlight.sched_block_minutes / 60)}h {nextFlight.sched_block_minutes % 60}m</span>
                    </div>
                  )}
                  {nextFlight.actual_out_time && (
                    <div className="flex justify-between">
                      <span>Actual Out</span>
                      <span className="font-mono">{getTimeFromZone(nextFlight.actual_out_time)}</span>
                    </div>
                  )}
                  {nextFlight.actual_in_time && (
                    <div className="flex justify-between">
                      <span>Actual In</span>
                      <span className="font-mono">{getTimeFromZone(nextFlight.actual_in_time)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Flight Type</span>
                    <span className="font-mono">{nextFlight.flight_type || 'Commercial'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tail Number</span>
                    <span className="font-mono">{nextFlight.tail_number || '—'}</span>
                  </div>
                  {nextFlight.is_international && (
                    <div className="text-purple-300 text-xs">🌍 International Flight</div>
                  )}
                </div>
              )}
            </div>

            {/* ✅ NEXT HOTEL - 3-LEVEL ACCORDION */}
            <div 
              className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 cursor-pointer hover:bg-slate-900/60 transition-colors"
              onClick={() => setExpandedHotel(toggleExpandLevel(expandedHotel))}
            >
              {/* Summary (Always Visible) */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Next Hotel</div>
                  <div className="text-white font-semibold">
                    {nextHotelDutyPeriod?.layover_hotel_name
                      ? `${nextHotelDutyPeriod.layover_hotel_name} • ${nextHotelDutyPeriod.layover_location || 'Layover'}`
                      : 'No layover hotel scheduled'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-purple-300 text-sm">
                    {nextHotelDutyPeriod?.layover_duration?.minutes
                      ? `${Math.floor(nextHotelDutyPeriod.layover_duration.minutes / 60)}h ${nextHotelDutyPeriod.layover_duration.minutes % 60}m`
                      : '--'}
                  </div>
                  {/* Chevron Icon */}
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform ${
                      expandedHotel === 1 ? 'rotate-180' : expandedHotel === 2 ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Level 1: Basic Details (Hotel, Location, Phone) */}
              {expandedHotel >= 1 && nextHotelDutyPeriod?.layover_hotel_name && (
                <div className="mt-3 space-y-2 text-sm text-gray-300 border-t border-slate-600 pt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Hotel</span>
                    <span>{nextHotelDutyPeriod.layover_hotel_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Location</span>
                    <span>{nextHotelDutyPeriod.layover_location || '—'}</span>
                  </div>
                  {nextHotelDutyPeriod.layover_phone && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Phone</span>
                      <span className="text-cyan-300">{nextHotelDutyPeriod.layover_phone}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Level 2: Granular Details (Layover Duration, Room Info, Amenities) */}
              {expandedHotel === 2 && nextHotelDutyPeriod?.layover_hotel_name && (
                <div className="mt-2 space-y-2 text-xs text-gray-400 bg-slate-950/50 p-2 rounded border border-slate-600">
                  <div className="text-cyan-300 font-semibold mb-2">Extended Details</div>
                  <div className="flex justify-between">
                    <span>Layover Start</span>
                    <span className="font-mono">{formatInstantTime(nextHotelDutyPeriod.release)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Layover End</span>
                    <span className="font-mono">{formatInstantTime(nextHotelDutyPeriod.report)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Layover</span>
                    <span className="font-mono">
                      {nextHotelDutyPeriod?.layover_duration?.minutes
                        ? `${Math.floor(nextHotelDutyPeriod.layover_duration.minutes / 60)}h ${nextHotelDutyPeriod.layover_duration.minutes % 60}m`
                        : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Layover Type</span>
                    <span className="font-mono">
                      {nextHotelDutyPeriod?.is_overnight ? 'Overnight' : 'Turnaround'}
                    </span>
                  </div>
                  {nextHotelDutyPeriod?.layover_address && (
                    <div className="flex justify-between">
                      <span>Address</span>
                      <span className="font-mono text-right">{nextHotelDutyPeriod.layover_address}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ✅ NEXT DUTY DAY - 3-LEVEL ACCORDION */}
            <div 
              className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 cursor-pointer hover:bg-slate-900/60 transition-colors"
              onClick={() => setExpandedDutyDay(toggleExpandLevel(expandedDutyDay))}
            >
              {/* Summary (Always Visible) */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Next Duty Day</div>
                  <div className="text-white font-semibold">
                    {nextDutyPeriod
                      ? `Duty ${rotationPreview.currentDutyIndex + 2} • ${formatInstantDate(nextDutyPeriod.duty_date || nextDutyPeriod.report)}`
                      : 'No additional duty periods'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-cyan-400 text-sm font-mono">
                    {nextDutyPeriod ? formatInstantTime(nextDutyPeriod.report) : '--:--'}
                  </div>
                  {/* Chevron Icon */}
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform ${
                      expandedDutyDay === 1 ? 'rotate-180' : expandedDutyDay === 2 ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Level 1: Basic Details (Report, Release, Legs Count) */}
              {expandedDutyDay >= 1 && nextDutyPeriod && (
                <div className="mt-3 space-y-2 text-sm text-gray-300 border-t border-slate-600 pt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Report</span>
                    <span className="font-mono">{formatInstantTime(nextDutyPeriod.report)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Release</span>
                    <span className="font-mono">{formatInstantTime(nextDutyPeriod.release)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Legs</span>
                    <span className="text-cyan-300">{nextDutyPeriod.legs?.length || 0}</span>
                  </div>
                </div>
              )}

              {/* Level 2: Granular Details (Full Legs List, Block Time, Duty Time) */}
              {expandedDutyDay === 2 && nextDutyPeriod && (
                <div className="mt-2 space-y-2 text-xs text-gray-400 bg-slate-950/50 p-2 rounded border border-slate-600">
                  <div className="text-cyan-300 font-semibold mb-2">Flights & Details</div>
                  
                  {/* Flights List */}
                  {nextDutyPeriod.legs?.length > 0 && (
                    <div className="space-y-2 pb-2 border-b border-slate-600">
                      {nextDutyPeriod.legs.map((leg, index) => (
                        <div key={`${leg.flight_number || 'flight'}-${index}`} className="text-gray-400">
                          <div className="flex justify-between">
                            <span>{leg.flight_number || 'Flight'}</span>
                            <span className="font-mono">{leg.departure || 'DEP'} → {leg.arrival || 'ARR'}</span>
                          </div>
                          {leg.sched_block_minutes && (
                            <div className="flex justify-between text-xs text-gray-500 ml-2">
                              <span>Block</span>
                              <span className="font-mono">{Math.floor(leg.sched_block_minutes / 60)}h {leg.sched_block_minutes % 60}m</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Duty Period Totals */}
                  <div className="space-y-1 pt-1">
                    {nextDutyPeriod.block_minutes && (
                      <div className="flex justify-between">
                        <span>Total Block</span>
                        <span className="font-mono">{Math.floor(nextDutyPeriod.block_minutes / 60)}h {nextDutyPeriod.block_minutes % 60}m</span>
                      </div>
                    )}
                    {nextDutyPeriod.duty_minutes && (
                      <div className="flex justify-between">
                        <span>Total Duty</span>
                        <span className="font-mono">{Math.floor(nextDutyPeriod.duty_minutes / 60)}h {nextDutyPeriod.duty_minutes % 60}m</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Duration</span>
                      <span className="font-mono">
                        {(() => {
                          const start = new Date(nextDutyPeriod.report);
                          const end = new Date(nextDutyPeriod.release);
                          const diffMs = end - start;
                          const hours = Math.floor(diffMs / (1000 * 60 * 60));
                          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          return `${hours}h ${minutes}m`;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>


          {/* ✅ ROTATION DETAILS TIMES */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="text-cyan-400 text-sm uppercase mb-3 font-semibold tracking-wider text-center">
              Rotation Details Times
            </div>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-gray-400">Report Time:</span>
                <span className="text-cyan-400 font-mono font-bold">
                  {compiledData?.compiled_json?.duty_periods?.[0]?.report?.local || '--:--'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-gray-400">Scheduled Block:</span>
                <span className="text-cyan-400 font-mono font-bold">
                  {(() => {
                    const mins = compiledData?.compiled_json?.total_sched_block_minutes || 0;
                    const hrs = Math.floor(mins / 60);
                    const min = mins % 60;
                    return `${hrs}:${min.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-gray-400">Duty Time:</span>
                <span className="text-cyan-400 font-mono font-bold">
                  {(() => {
                    const mins = compiledData?.compiled_json?.total_duty_minutes || 0;
                    const hrs = Math.floor(mins / 60);
                    const min = mins % 60;
                    return `${hrs}:${min.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-400">Equipment:</span>
                <span className="text-cyan-400 font-mono font-bold">
                  {getMeta().equipment || compiledData?.compiled_json?.duty_periods?.[0]?.legs?.[0]?.aircraft_type || '73N'}
                </span>
              </div>
            </div>
          </div>

          {/* ✅ COMPLIANCE STATUS - CLICKABLE TILE */}
          {complianceData && (
            <button
              onClick={() => setShowFAR117Modal(true)}
              className={`w-full border-2 rounded-lg p-6 text-center transition-all hover:shadow-lg ${
                complianceData.is_compliant 
                  ? 'border-green-500/50 bg-green-500/10 hover:bg-green-500/20' 
                  : 'border-red-500/50 bg-red-500/10 hover:bg-red-500/20'
              }`}
            >
              {/* Line 1: Header */}
              <div className="text-sm font-semibold text-gray-300 mb-3">
                FAR 117 Rotation Analysis
              </div>
              
              {/* Line 2: Status with indicator */}
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className={`w-4 h-4 rounded-full ${complianceData.is_compliant ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`text-lg font-bold ${complianceData.is_compliant ? 'text-green-400' : 'text-red-400'}`}>
                  {complianceData.is_compliant ? '✓ Compliant' : '✗ Violations'}
                </span>
              </div>
              
              {/* Line 3: Violations count */}
              <div className="text-xs text-gray-400">
                {complianceData.violation_count === 0 
                  ? '0 Violations Detected'
                  : `${complianceData.violation_count} Violation${complianceData.violation_count !== 1 ? 's' : ''} Detected`
                }
              </div>
            </button>
          )}

          {/* ✅ QUICK ACTIONS */}
          <div className="flex gap-3 pt-2">
            <button 
              onClick={handleRefresh}
              disabled={loading}
              className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold py-2 rounded"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

        </div>
      </div>

      {/* Weather Modal - Slide in from side of button clicked */}
      {selectedAirport && (
        <WeatherModal
          isOpen={weatherModalOpen}
          onClose={handleWeatherClose}
          airportCode={selectedAirport}
          slideFrom={weatherSlideFrom}
        />
      )}

      {/* FAR 117 Detail Modal */}
      <FAR117DetailModal
        open={showFAR117Modal}
        onClose={() => setShowFAR117Modal(false)}
        dutyPeriod={currentDutyPeriod}
        rotation={compiledData}
      />

      {/* Flight Details Modal - OOOI Times */}
      <FlightDetailsModal
        show={showFlightModal}
        onClose={() => setShowFlightModal(false)}
        currentFlight={currentFlight}
        previousFlight={previousFlight}
      />

    </div>
  );
};

export default CurrentTrip;
